import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import map from 'lodash/map';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';
import {connect} from 'react-redux';
import {compose} from 'redux';
import classNames from 'classnames';
import moment from 'moment';
import {window} from 'ssr-window';
import Alert from '@spothero/ui/alert';
import Button from '@spothero/ui/button';
import GooglePlacesSearchInput from '@spothero/ui/google-places-search-input';
import SearchInputMB from 'search/mapbox/SearchInputMB';
import Label from '@spothero/ui/label';
import PoweredByGoogle from '@spothero/ui/powered-by-google';
import TextButton from '@spothero/ui/text-button';
import EnvironmentUtils from '@spothero/utils/environment';
import StorageUtils from '@spothero/utils/storage';
import SearchAPI from 'api/search';
import withConfig from 'config/withConfig';
import DateTimePickers from 'common/date-time-pickers/DateTimePickers';
import {push} from 'router/router-actions';
import {updateTimes} from 'search-request/search-request-actions';
import {resetSelectedSpot} from 'spot/spot-actions';
import {cancelSpotRequests} from 'spots/spots-actions';
import TimeUtils from 'utils/time';
import SearchGeocoderUtils from '../search-geocoder-utils';
import matchPlaces from 'search/mapbox/search-geocoder-utils-MB';
import SearchTermToggle from './SearchTermToggle';

const DidYouMeanModal = loadable(() => import('./DidYouMeanModal'));

class SearchControls extends Component {
    static propTypes = {
        isMobile: PropTypes.bool.isRequired,
        apiDateTimeFormat: PropTypes.string.isRequired,
        className: PropTypes.string,
        isCanadaEnabled: PropTypes.bool,
        isMonthly: PropTypes.bool,
        isAirport: PropTypes.bool,
        includeAirport: PropTypes.bool,
        isEvent: PropTypes.bool.isRequired,
        starts: PropTypes.string,
        ends: PropTypes.string,
        placeholder: PropTypes.string,
        searchButtonText: PropTypes.string,
        searchString: PropTypes.string,
        originalSearchString: PropTypes.string,
        usersLatitude: PropTypes.string,
        usersLongitude: PropTypes.string,
        updateSearchTimes: PropTypes.func.isRequired,
        cancelAllSpotRequests: PropTypes.func.isRequired,
        focusSearchInput: PropTypes.bool,
        showTerm: PropTypes.bool,
        datetimeOnly: PropTypes.bool,
        showSpotUnavailable: PropTypes.bool,
        showDateTimes: PropTypes.bool,
        showFindParkingButton: PropTypes.bool,
        isDefaultTimes: PropTypes.bool,
        onSearchClick: PropTypes.func,
        onClosePanelClick: PropTypes.func,
        onSuggestSelect: PropTypes.func,
        clearSelectedSpot: PropTypes.func.isRequired,
        pushTo: PropTypes.func.isRequired,
        searchBounds: PropTypes.shape({
            swLat: PropTypes.number,
            swLng: PropTypes.number,
            neLat: PropTypes.number,
            neLng: PropTypes.number,
        }),
        defaultToFindParkingNearMe: PropTypes.bool,
        isSearchPickersExperiment: PropTypes.bool,
        showActions: PropTypes.bool,
        showCancel: PropTypes.bool,
        onDateTimeChange: PropTypes.func,
        onCancelClick: PropTypes.func,
        mapBoxTest: PropTypes.string,
    };
    static defaultProps = {
        showTerm: true,
        placeholder: 'Address, Event, Venue, Airport, or City',
        searchButtonText: 'Search',
        searchString: '',
        showDateTimes: true,
    };

    constructor(props) {
        super(props);

        const {
            isMonthly,
            isAirport,
            starts,
            ends,
            searchString,
            searchBounds,
        } = props;
        const isBrowser = EnvironmentUtils.isBrowser();

        this._gmaps = isBrowser ? window.google.maps : null;
        this._mobileModalAnimationTime = 500;
        this._bounds =
            isBrowser && searchBounds
                ? new this._gmaps.LatLngBounds(
                      new this._gmaps.LatLng(
                          searchBounds.swLat,
                          searchBounds.swLng
                      ),
                      new this._gmaps.LatLng(
                          searchBounds.neLat,
                          searchBounds.neLng
                      )
                  )
                : null;
        this._queuedLocationData = null;

        this.state = {
            starts,
            ends,
            isSearching: false,
            isValidStart: true,
            searchAddressModalResults: null,
            country: ['us'],
            currentSearch: searchString,
            isSearchEmpty: isEmpty(searchString),
            hasChangedDateTime: false,
            hasChangedDestination: false,
            prevIsMonthly: isMonthly,
            prevIsAirport: isAirport,
        };
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const {isCanadaEnabled, isMonthly, isAirport} = nextProps;
        const {country: prevCountry, prevIsMonthly, prevIsAirport} = prevState;
        let nextState = {};

        if (isMonthly !== prevIsMonthly || isAirport !== prevIsAirport) {
            nextState = {
                ...nextState,
                searchAddressModalResults: null,
                prevIsMonthly: isMonthly,
                prevIsAirport: isAirport,
            };
        }

        if (Boolean(prevCountry.length - 1) !== Boolean(isCanadaEnabled)) {
            nextState.country = ['us', isCanadaEnabled && 'ca'].filter(Boolean);
        }

        return Object.keys(nextState).length ? nextState : null;
    }

    componentDidMount() {
        this._manageKeyListeners();
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (prevProps.showActions !== this.props.showActions) {
            this._manageKeyListeners();
        }
    }

    componentWillUnmount() {
        this._manageKeyListeners(true);
    }
    _onClearClick = () => {
        this._onSearchInputChange({
            target: {
                value: '',
            },
        });
    };

    _onSearchTermChange = () => {
        const {isMobile} = this.props;

        this.setState({
            hasChangedDateTime: isMobile,
        });
    };

    _onSearchInputChange = evt => {
        const value = evt.target.value;

        this.setState({
            currentSearch: value,
            isSearchEmpty: isEmpty(value),
            hasChangedDestination: true,
        });
    };

    _onSuggestSelect = ({
        label,
        location,
        id,
        place_id: placeId,
        reference,
        url,
    }) => {
        const {
            isSearchPickersExperiment,
            onSuggestSelect,
            mapBoxTest,
        } = this.props;
        const timeout = isSearchPickersExperiment
            ? 0
            : this._mobileModalAnimationTime;

        let submitParams = {};

        /* eslint-disable camelcase */
        if (mapBoxTest === 'noplaces') {
            submitParams = {
                search_string: label,
                latitude: location.lat,
                longitude: location.lng,
            };
        } else {
            submitParams = {
                search_string: label,
                latitude: location.lat,
                longitude: location.lng,
                google_places_id: id,
                google_places_place_id: placeId,
                google_places_reference: reference,
            };
        }
        /* eslint-enable camelcase */

        this._submitSearch(submitParams, true);

        if (onSuggestSelect) {
            setTimeout(() => {
                // don't call this until the mobile panel has been allowed sufficient time to close and UI is cleared out
                onSuggestSelect();
            }, timeout);
        }
    };

    _onSuggestSelectMB = ({description, id, latitude, longitude}) => {
        const {isSearchPickersExperiment, onSuggestSelect} = this.props;
        const timeout = isSearchPickersExperiment
            ? 0
            : this._mobileModalAnimationTime;

        this._submitSearch(
            {
                search_string: description, // eslint-disable-line camelcase
                description,
                id,
                latitude,
                longitude,
            },
            true
        );

        if (onSuggestSelect) {
            setTimeout(() => {
                // don't call this until the mobile panel has been allowed sufficient time to close and UI is cleared out
                onSuggestSelect();
            }, timeout);
        }
    };

    _onDateTimeChange = ({start, end, isValidStart}) => {
        const {
            apiDateTimeFormat,
            isDefaultTimes,
            onDateTimeChange,
        } = this.props;
        const {starts, ends} = this.state;
        const newStarts = start.format(apiDateTimeFormat);
        const newEnds = end.format(apiDateTimeFormat);
        const nextState = {
            starts: newStarts,
            ends: newEnds,
            isValidStart,
        };

        if (!isDefaultTimes || (starts !== newStarts || ends !== newEnds)) {
            nextState.hasChangedDateTime = true;
        }

        this.setState(nextState);

        if (onDateTimeChange) {
            onDateTimeChange();
        }
    };

    _onNoAddressFoundModalHidden = result => {
        const {onClosePanelClick, onSearchClick} = this.props;
        const {starts, ends} = this.state;

        this.setState({
            searchAddressModalResults: null,
        });

        // if multiple results are shown in the dialog then clicking a result should fire the selection process
        if (!isUndefined(result)) {
            this._onSearchAddressSelected(result);

            if (onSearchClick) {
                onSearchClick({
                    starts,
                    ends,
                });
            }
        }

        if (onClosePanelClick) {
            setTimeout(() => {
                // don't call this until the mobile panel has been allowed sufficient time to close and UI is cleared out
                onClosePanelClick();
            }, this._mobileModalAnimationTime);
        }
    };

    _onSearchAddressSelected = results => {
        const {mapBoxTest} = this.props;
        let data = results;

        if (isArray(results)) {
            if (results.length === 1) {
                if (mapBoxTest === 'mapbox') {
                    data = results[0];
                } else {
                    data = SearchGeocoderUtils.getAddressData(results[0]);
                }
            } else {
                if (mapBoxTest !== 'mapbox') {
                    data = map(data, result => {
                        return SearchGeocoderUtils.getAddressData(result);
                    });
                }

                // no results or more than one result found, show modal
                this.setState({
                    searchAddressModalResults: data,
                });

                return;
            }
        }

        /* eslint-disable camelcase */
        const submitParams = {
            search_string: data.search_string,
            latitude: data.latitude,
            longitude: data.longitude,
            ...(mapBoxTest === 'baseline' && {
                google_places_id: data.places_id,
                google_places_place_id: data.places_place_id,
                google_places_reference: data.places_reference,
            }),
        };
        /* eslint-enable camelcase */

        this._submitSearch(submitParams);
    };

    _onSearchClick = () => {
        const {
            onSearchClick,
            updateSearchTimes,
            defaultToFindParkingNearMe,
            pushTo,
            isMonthly,
            mapBoxTest,
        } = this.props;
        const {
            starts,
            ends,
            hasChangedDateTime,
            hasChangedDestination,
        } = this.state;
        const value = !isEmpty(this._searchField)
            ? this._searchField.node.value
            : null;

        if (defaultToFindParkingNearMe && isEmpty(value)) {
            pushTo(`/${isMonthly ? 'monthly-' : ''}parking-near-me/`);
        } else {
            if (hasChangedDestination) {
                if (mapBoxTest === 'mapbox') {
                    matchPlaces(value).then(res => {
                        this._onSearchAddressSelected(res);
                    });
                } else {
                    const geocoder = new this._gmaps.Geocoder();

                    geocoder.geocode(
                        {
                            address: value,
                        },
                        (res, status) => {
                            let results = res;

                            if (status !== this._gmaps.GeocoderStatus.OK) {
                                results = [];
                            }

                            this._onSearchAddressSelected(results);
                        }
                    );
                }
            }

            if (onSearchClick) {
                const searchClickParams = {
                    starts,
                    ends,
                };

                if (hasChangedDateTime) {
                    searchClickParams.callback = () => {
                        updateSearchTimes({
                            starts,
                            ends,
                        });
                    };
                }

                onSearchClick(searchClickParams);
            } else if (hasChangedDateTime) {
                updateSearchTimes({
                    starts,
                    ends,
                });
            }
        }

        this.setState({
            hasChangedDateTime: false,
            hasChangedDestination: false,
        });
    };

    _onKeyDown = evt => {
        if (evt.which === 13) {
            // enter
            evt.preventDefault();

            this._onSearchClick();
        } else if (evt.which === 27) {
            // escape
            evt.preventDefault();

            this.props.onCancelClick();
        }
    };

    _manageKeyListeners(forceRemove) {
        const {showActions} = this.props;

        if (!showActions || forceRemove) {
            // remove key listeners for update and cancel buttons
            window.removeEventListener('keydown', this._onKeyDown);
        } else if (showActions) {
            // add key listeners for update and cancel buttons
            window.addEventListener('keydown', this._onKeyDown);
        }
    }

    async _submitSearch(locationData, isFromSuggestSelection = false) {
        const {
            isMonthly,
            originalSearchString,
            usersLatitude,
            usersLongitude,
            cancelAllSpotRequests,
            clearSelectedSpot,
            pushTo,
            isDefaultTimes,
            isSearchPickersExperiment,
        } = this.props;
        const {starts, ends, hasChangedDateTime} = this.state;

        if (isSearchPickersExperiment && isFromSuggestSelection) {
            // save the location data and don't run a search in the experiment because the user has to click Update Search in the UI
            this._queuedLocationData = locationData;

            return;
        }

        const params = {
            ...(this._queuedLocationData
                ? this._queuedLocationData
                : locationData),
            csrfmiddlewaretoken: StorageUtils.get('csrftoken', 'cookie'),
            monthly: isMonthly,
            original_search_string: originalSearchString, // eslint-disable-line camelcase
            users_latitude: usersLatitude, // eslint-disable-line camelcase
            users_longitude: usersLongitude, // eslint-disable-line camelcase
            starts: isDefaultTimes && !hasChangedDateTime ? null : starts,
            ends:
                isMonthly || (isDefaultTimes && !hasChangedDateTime)
                    ? null
                    : ends,
        };

        this.setState({
            isSearching: true,
        });

        cancelAllSpotRequests();

        const {
            data: {data},
        } = await SearchAPI.cleanParams(params);

        this._queuedLocationData = null;

        this.setState({
            isSearching: false,
            hasChangedDateTime: false,
            hasChangedDestination: false,
        });

        clearSelectedSpot();

        pushTo(data.search_url, {cleanedParams: data});
    }

    render() {
        const {
            className,
            isMonthly,
            isEvent,
            starts,
            ends,
            includeAirport,
            placeholder,
            searchButtonText,
            searchString,
            showTerm,
            showDateTimes,
            datetimeOnly,
            showFindParkingButton,
            showSpotUnavailable,
            focusSearchInput,
            onClosePanelClick,
            isSearchPickersExperiment,
            showActions,
            showCancel,
            onCancelClick,
            mapBoxTest,
        } = this.props;
        const {
            country,
            isSearching,
            isValidStart,
            searchAddressModalResults,
            isSearchEmpty,
            hasChangedDateTime,
            currentSearch,
            starts: tempStarts,
            ends: tempEnds,
        } = this.state;
        const classes = classNames(
            'SearchControls',
            {'SearchControls-with-find-parking': showFindParkingButton},
            {'SearchControls-with-airport': includeAirport},
            {'monthly-results': isMonthly},
            {EventSearch: isEvent},
            className
        );
        const searchInputClasses = classNames({
            'GooglePlacesSearchInput-searching': isSearching,
        });
        const hasChangedSearchString = currentSearch !== searchString;

        return (
            <div className={classes}>
                <div className="container">
                    {showSpotUnavailable && (
                        <Alert type="danger" className="text-center">
                            This spot is not available at the selected times.
                            Adjust your times or&nbsp;
                            <TextButton onClick={onClosePanelClick}>
                                view available spots.
                            </TextButton>
                        </Alert>
                    )}
                    {showTerm && (
                        <SearchTermToggle
                            isSearching={isSearching}
                            onTermChange={this._onSearchTermChange}
                        />
                    )}
                    {!datetimeOnly && mapBoxTest === 'mapbox' && (
                        <SearchInputMB
                            className={searchInputClasses}
                            ref={node => {
                                this._searchField = node;
                            }}
                            placeholder={placeholder}
                            autoFocus={focusSearchInput}
                            country={country}
                            onSuggestionSelect={this._onSuggestSelectMB}
                            bounds={this._bounds}
                            loading={isSearching}
                            defaultValue={searchString}
                            disabled={isSearching}
                            onClear={this._onClearClick}
                            onChange={this._onSearchInputChange}
                            label={
                                isSearchPickersExperiment ? (
                                    <Label>
                                        {isMonthly ? 'Monthly' : 'Find'} Parking
                                        Near
                                    </Label>
                                ) : null
                            }
                        />
                    )}
                    {!datetimeOnly && mapBoxTest !== 'mapbox' && (
                        <GooglePlacesSearchInput
                            className={searchInputClasses}
                            ref={node => {
                                this._searchField = node;
                            }}
                            placeholder={placeholder}
                            autoFocus={focusSearchInput}
                            country={country}
                            onSuggestionSelect={this._onSuggestSelect}
                            poweredByGoogleFooter={<PoweredByGoogle />}
                            googleMaps={this._gmaps}
                            bounds={this._bounds}
                            loading={isSearching}
                            defaultValue={searchString}
                            disabled={isSearching}
                            onClear={this._onClearClick}
                            onChange={this._onSearchInputChange}
                            label={
                                isSearchPickersExperiment ? (
                                    <Label>Find Parking Near</Label>
                                ) : null
                            }
                        />
                    )}
                    {showDateTimes && (
                        <div className="DateTime">
                            <div className="SearchControls-datetime">
                                <DateTimePickers
                                    isMonthly={isMonthly}
                                    starts={starts}
                                    ends={ends}
                                    startDateLabel={
                                        isSearchPickersExperiment ? (
                                            <Label>Enter After</Label>
                                        ) : null
                                    }
                                    endDateLabel={
                                        isSearchPickersExperiment ? (
                                            <Label>Exit Before</Label>
                                        ) : null
                                    }
                                    monthlyStartDateLabel={
                                        <Label className="monthly-label">
                                            Monthly Parking Starting
                                        </Label>
                                    }
                                    onChange={this._onDateTimeChange}
                                />
                                {showActions && (
                                    <>
                                        {!isMonthly && (
                                            <span className="SearchControls-duration">
                                                {TimeUtils.prettyDuration(
                                                    moment(tempEnds).diff(
                                                        moment(tempStarts)
                                                    )
                                                )}{' '}
                                                total
                                            </span>
                                        )}
                                        <Button
                                            className="SearchControls-submit"
                                            color="primary"
                                            loading={isSearching}
                                            disabled={
                                                !isValidStart ||
                                                ((!hasChangedSearchString ||
                                                    isSearchEmpty) &&
                                                    !hasChangedDateTime) ||
                                                isSearching
                                            }
                                            onClick={this._onSearchClick}
                                        >
                                            {searchButtonText}
                                        </Button>
                                        {showCancel && (
                                            <TextButton
                                                className="SearchControls-cancel"
                                                disabled={isSearching}
                                                block
                                                onClick={onCancelClick}
                                            >
                                                Cancel
                                            </TextButton>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                    {showFindParkingButton && (
                        <Button
                            className="SearchControls-find-parking"
                            color="primary"
                            onClick={this._onSearchClick}
                            disabled={isSearching}
                            loading={isSearching}
                        >
                            Find Parking
                        </Button>
                    )}
                </div>
                {searchAddressModalResults && (
                    <DidYouMeanModal
                        results={searchAddressModalResults}
                        onHidden={this._onNoAddressFoundModalHidden}
                    />
                )}
            </div>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    const {
        search: {
            data: {searchType, isDefaultTimes},
        },
        searchRequest: {
            monthly,
            starts,
            ends,
            airport,
            search_string: searchString,
            original_search_string: originalSearchString,
            users_latitude: usersLatitude,
            users_longitude: usersLongitude,
            pageType,
        },
        city: {data: city},
        destination: {data: destination},
        user: {
            data: {
                experiments: {
                    web_canada: {isFeatureEnabled: isCanadaEnabled},
                    mapbox_test: mapBoxTest,
                },
            },
        },
    } = state;
    const {
        display_name: cityDisplayName,
        state: displayState,
        search_bounds: searchBounds,
    } = city;
    let derivedSearchString = searchString ? searchString : '';

    if (ownProps.isSearchPickersExperiment && isEmpty(derivedSearchString)) {
        if (pageType === 'city') {
            derivedSearchString = `${cityDisplayName}, ${displayState}`;
        } else if (pageType === 'destination' || pageType === 'event') {
            derivedSearchString = `${destination.title}, ${cityDisplayName}, ${displayState}`;
        }
    }

    return {
        isCanadaEnabled,
        isMonthly: monthly,
        isEvent: searchType === 'event',
        isDefaultTimes,
        starts,
        ends,
        isAirport: airport,
        searchString: derivedSearchString,
        originalSearchString,
        usersLatitude,
        usersLongitude,
        searchBounds: searchBounds && {
            swLat: searchBounds.sw_lat,
            swLng: searchBounds.sw_lng,
            neLat: searchBounds.ne_lat,
            neLng: searchBounds.ne_lng,
        },
        mapBoxTest,
    };
};

const mapDispatchToProps = {
    updateSearchTimes: updateTimes,
    cancelAllSpotRequests: cancelSpotRequests,
    clearSelectedSpot: resetSelectedSpot,
    pushTo: push,
};

const enhance = compose(
    withConfig(['apiDateTimeFormat', 'isMobile']),
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
);

export default enhance(SearchControls);
